import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, CheckCircle, Coffee, ArrowLeft, TrendingUp } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { fetchShop, fetchCustomerShopAccounts, fetchCustomerProfile, queryRecentPurchases } from '../../services/sui-service';
import { createStampRequest, getStampCooldownRemaining } from '../../services/stamp-service';

function ShopDetail() {
  const { id: shopId } = useParams();
  const navigate = useNavigate();
  const { userAddress } = useAuthStore();
  const [shop, setShop] = useState<any>(null);
  const [userAccount, setUserAccount] = useState<any>(null);
  const [recentPurchases, setRecentPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stampRequested, setStampRequested] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);

  useEffect(() => {
    if (shopId) loadShopData();
  }, [shopId, userAddress]);

  async function loadShopData() {
    setLoading(true);
    try {
      // Fetch shop details
      const shopObj = await fetchShop(shopId!);
      const fields = (shopObj.data?.content as any)?.fields || {};
      const parsedShop = {
        id: shopObj.data?.objectId,
        owner: fields.owner,
        name: fields.name || 'Unknown Shop',
        description: fields.description || '',
        location: fields.location || '',
        verified: fields.verified || false,
        active: fields.active !== false,
        totalPurchases: parseInt(fields.total_purchases) || 0,
        pointsMultiplier: parseInt(fields.points_multiplier) || 100,
      };
      setShop(parsedShop);

      // Fetch user's account at this shop (if logged in)
      if (userAddress) {
        const [accounts, profileRaw] = await Promise.all([
          fetchCustomerShopAccounts(userAddress),
          fetchCustomerProfile(userAddress),
        ]);

        if (profileRaw) {
          setProfileId((profileRaw as any).data?.objectId || null);
        }

        const myAccount = accounts.find((acc: any) => {
          const accFields = (acc.data?.content as any)?.fields || {};
          return accFields.shop === parsedShop.owner;
        });
        if (myAccount) {
          const accFields = (myAccount.data?.content as any)?.fields || {};
          setUserAccount({
            id: myAccount.data?.objectId,
            points: parseInt(accFields.points) || 0,
            purchases: parseInt(accFields.purchases) || 0,
            lastPurchase: parseInt(accFields.last_purchase_timestamp) || 0,
          });
        }

        // Fetch recent purchases at this shop
        const recent = await queryRecentPurchases(parsedShop.owner, 5);
        const myRecent = recent.filter((e: any) => e.parsedJson?.customer === userAddress);
        setRecentPurchases(myRecent);
      }
    } catch (err) {
      console.error('Failed to load shop:', err);
    }
    setLoading(false);
  }

  async function handleRequestStamp() {
    if (!userAddress || !shop) return;
    const accountId = userAccount?.id || '';
    await createStampRequest({
      customerAddress: userAddress,
      shopId: shop.id,
      shopOwnerAddress: shop.owner,
      customerShopAccountId: accountId,
      customerProfileId: profileId || '',
    });
    setStampRequested(true);
    setTimeout(() => setStampRequested(false), 30000);
  }

  const cooldownRemaining = userAddress && shop
    ? getStampCooldownRemaining(userAddress, shop.owner)
    : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="brutalist-card h-64 animate-pulse bg-gray-100" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="brutalist-card h-24 animate-pulse bg-gray-100" />)}
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="brutalist-card text-center py-12">
        <Coffee size={64} className="mx-auto mb-4 text-gray-300" />
        <p className="text-gray-600 text-lg">Shop not found</p>
        <button onClick={() => navigate('/shops')} className="brutalist-button mt-4">
          <ArrowLeft size={20} className="inline mr-2" />
          Back to Shops
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate('/shops')} className="brutalist-button flex items-center gap-2">
        <ArrowLeft size={20} />
        Back to Shops
      </button>

      {/* Shop Header */}
      <div className="brutalist-card">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <div className="p-8 bg-accent text-white brutalist-border flex items-center justify-center aspect-square">
              <Coffee size={120} />
            </div>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="font-heading text-4xl mb-2">{shop.name}</h1>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={20} className="text-accent" />
                  <span className="text-gray-700">{shop.location}</span>
                </div>
              </div>
              {shop.verified && (
                <div className="flex items-center gap-2 px-3 py-2 bg-success text-white font-bold">
                  <CheckCircle size={20} />
                  VERIFIED
                </div>
              )}
            </div>

            <p className="text-gray-700 leading-relaxed">{shop.description}</p>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t-3 border-black">
              {/* Aggregate volume is business-sensitive — members only
                  (DISCOVERY_VISIBILITY_DECISION.md leak #3). */}
              {userAccount && (
                <div>
                  <p className="text-sm text-gray-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-accent">{shop.totalPurchases.toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Points Rate</p>
                <p className="text-2xl font-bold text-primary">{shop.pointsMultiplier / 100}x</p>
              </div>
            </div>

            {/* Request Stamp Button */}
            <div className="space-y-2">
              {stampRequested || cooldownRemaining > 0 ? (
                <div className="w-full brutalist-border bg-yellow-50 text-center py-4 font-bold text-accent animate-pulse">
                  ⏳ Waiting for barista to approve your stamp...
                </div>
              ) : (
                <button onClick={handleRequestStamp} className="w-full brutalist-button-primary text-lg py-4">
                  <Coffee size={24} className="inline mr-2" />
                  Request Stamp
                </button>
              )}
              <p className="text-xs text-gray-600 text-center">
                Tap to send a stamp request to the barista for approval
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* User Stats at This Shop */}
      <div className="brutalist-card bg-background">
        <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
          <TrendingUp size={28} />
          Your Stats Here
        </h2>

        {userAccount ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="brutalist-card bg-white">
              <p className="text-sm text-gray-600 mb-1">Purchases</p>
              <p className="text-3xl font-bold text-accent">{userAccount.purchases}</p>
            </div>
            <div className="brutalist-card bg-white">
              <p className="text-sm text-gray-600 mb-1">Points Earned</p>
              <p className="text-3xl font-bold text-success">{userAccount.points}</p>
            </div>
            <div className="brutalist-card bg-white">
              <p className="text-sm text-gray-600 mb-1">Points to Free Coffee</p>
              <p className="text-3xl font-bold text-primary">{Math.max(0, 100 - userAccount.points)}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Coffee size={48} className="mx-auto mb-2 text-gray-300" />
            <p>Get your first stamp to start earning points!</p>
          </div>
        )}
      </div>

      {/* Recent Purchases at This Shop */}
      {recentPurchases.length > 0 && (
        <div className="brutalist-card">
          <h2 className="font-heading text-2xl mb-4">Your Recent Visits</h2>
          <div className="space-y-3">
            {recentPurchases.map((event: any, index: number) => {
              const data = event.parsedJson || {};
              return (
                <div key={index} className="flex items-center justify-between p-4 brutalist-border bg-background">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-accent text-white brutalist-border">
                      <Coffee size={24} />
                    </div>
                    <div>
                      <p className="font-bold">
                        {new Date(parseInt(data.timestamp)).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(parseInt(data.timestamp)).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-accent">+{data.points_earned}</p>
                    <p className="text-xs text-gray-600">points</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Location Placeholder */}
      <div className="brutalist-card">
        <h2 className="font-heading text-2xl mb-4">Location</h2>
        <div className="aspect-video brutalist-border bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <MapPin size={48} className="mx-auto mb-2" />
            <p className="font-bold">{shop.location}</p>
            <p className="text-sm mt-2">Map integration coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShopDetail;
